import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import type { SignInFlowError } from '../../hooks/classify-auth-error';
import { AuthErrorMessage } from './AuthErrorMessage';
import { TOTP_CODE_LENGTH, TotpCodeInput } from './TotpCodeInput';

export interface TotpSetupFormProps {
  setupUri: string;
  sharedSecret: string;
  submitting: boolean;
  error: SignInFlowError | null;
  onSubmit: (code: string) => void;
  /** 送信ボタンの文言（既定は「登録」） */
  submitLabel?: string;
}

/**
 * 認証アプリの登録。登録用 URI の QR コードと、手入力用の文字列を並べて表示する。
 *
 * QR はブラウザ内で生成する（シークレットを外部へ送らない）。canvas に依存しない SVG で作る。
 */
export function TotpSetupForm({
  setupUri,
  sharedSecret,
  submitting,
  error,
  onSubmit,
  submitLabel = '登録',
}: TotpSetupFormProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    QRCode.toString(setupUri, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' })
      .then((svg) => {
        if (!cancelled) {
          setQrDataUrl(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
        }
      })
      .catch(() => {
        // QR を作れなくても手入力用の文字列で登録できる
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setupUri]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sharedSecret);
      setCopied(true);
    } catch {
      // クリップボードを使えない環境では、表示された文字列を手で写してもらう
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(code);
    setCode('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <AuthErrorMessage error={error} />

      <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
        <li>認証アプリで下の QR コードを読み取ってください。</li>
        <li>認証アプリに表示された 6 桁のコードを入力して「{submitLabel}」を押してください。</li>
      </ol>

      <div className="flex justify-center">
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="認証アプリ登録用の QR コード"
            className="size-48 rounded-lg bg-white p-2"
          />
        ) : (
          <div className="size-48 rounded-lg bg-muted/50 animate-pulse" />
        )}
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-1">
          QR コードを読み取れない場合は、次の文字列を認証アプリに入力してください。
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded-lg bg-muted/50 px-3 py-2 font-mono text-sm text-foreground">
            {sharedSecret}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 rounded-lg border border-input px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
          >
            {copied ? 'コピーしました' : 'コピー'}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border p-3 text-xs text-muted-foreground space-y-1">
        <p className="font-medium text-foreground">認証アプリをお持ちでない場合</p>
        <p>スマートフォン：Google Authenticator、Microsoft Authenticator など</p>
        <p>PC：1Password、KeePassXC など</p>
      </div>

      <TotpCodeInput value={code} onChange={setCode} />

      <button
        type="submit"
        disabled={submitting || code.length !== TOTP_CODE_LENGTH}
        className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? '確認中...' : submitLabel}
      </button>
    </form>
  );
}
